import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../common/types';
import { Cover } from '../../music24/components/primitives/Cover';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { catalogArtworkSrc } from '../catalog/catalogArtwork';
import { daysUntilRelease, formatCatalogDay, formatCatalogRelativeDay } from '../catalog/catalogDates';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the sheet; each part uses its own slice of it */
import styles from './SMLCreationSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CreationCatalogRowProps {
  item: CatalogItem;
  now: number;
  onPick: (item: CatalogItem) => void;
}

/**
 * One release out of the artist's catalog, with the one fact that decides what
 * a page for it would be: whether it is out.
 *
 * A release still ahead gets the day itself — that is the date the artist is
 * working towards. One already out gets how long ago, which is all anyone
 * needs to recognise it.
 */
const CreationCatalogRowComponent: React.FC<CreationCatalogRowProps> = ({ item, now, onPick }) => {
  const intl = useIntl();
  const pick = useCallback(() => onPick(item), [item, onPick]);

  const days = item.releaseDate === null ? undefined : daysUntilRelease(item.releaseDate, now);
  const isAhead = item.kind === CatalogItemKind.unreleased || (days !== undefined && days > 0);

  const pill = useMemo(() => {
    if (item.releaseDate === null) {
      return intl.formatMessage({ id: 'app.sml.create.row.no_date', defaultMessage: 'no date yet' });
    }
    if (isAhead) {
      return intl.formatMessage(
        { id: 'app.sml.create.row.out_on', defaultMessage: 'Out {day}' },
        { day: formatCatalogDay(intl, item.releaseDate) }
      );
    }
    return formatCatalogRelativeDay(intl, item.releaseDate, now) ?? '';
  }, [intl, isAhead, item.releaseDate, now]);

  return (
    <button type="button" className={styles.row} onClick={pick}>
      <Cover src={catalogArtworkSrc(item, 42)} label={item.title} size={42} />
      <span className={styles.rowInfo}>
        <span className={styles.rowName}>{item.title}</span>
        <span className={styles.rowSub}>
          {item.releaseType === null ? item.artistName : `${item.artistName} · ${item.releaseType}`}
        </span>
      </span>
      <span className={clsx(styles.pill, isAhead ? styles.pillUnreleased : styles.pillOut)}>{pill}</span>
      <FontAwesomeIcon icon={faChevronRight} className={styles.rowGo} />
    </button>
  );
};

export const CreationCatalogRow = TypedMemo(CreationCatalogRowComponent);
